/**
 * VOS signature images: fetches presigned URLs and displays Requester/Interpreter signatures.
 * Used in Meeting Summary (OSI) tab inside Client Submitted VOS and Interpreter Submitted VOS cards.
 */
import React, {useEffect, useState} from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

/** Single signature image: fetches presigned URL and renders in a card-style box with label. */
export const VOSSignatureImage: React.FC<{
  fileUrl?: string | null
  label: string
  className?: string
}> = ({fileUrl, label, className = ''}) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!fileUrl) return
    const token = localStorage.getItem('accessToken')
    const getPresignedUrl = async (url: string): Promise<string | null> => {
      const response = await axios.post(
        `${API_URL}/Appoinment/get-signed-url`,
        {fileUrl: url, expirationMinutes: 60},
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? {Authorization: `Bearer ${token}`} : {}),
          },
        }
      )
      if (response.status !== 200) return null
      return response.data?.signedUrl ?? null
    }
    setLoading(true)
    setError(null)
    getPresignedUrl(fileUrl)
      .then((url) => setSignedUrl(url ?? null))
      .catch(() => setError('Failed to load signature'))
      .finally(() => setLoading(false))
  }, [fileUrl])

  if (!fileUrl) return null

  return (
    <div className={className}>
      <div
        className='bg-white border border-gray-300 rounded d-flex align-items-center justify-content-center overflow-hidden'
        style={{minHeight: 100, maxHeight: 140}}
      >
        {loading && <span className='text-muted small'>Loading...</span>}
        {error && <span className='text-danger small'>{error}</span>}
        {signedUrl && !loading && (
          <img
            src={signedUrl}
            alt={label}
            style={{maxWidth: '100%', maxHeight: 120, objectFit: 'contain'}}
          />
        )}
        {!signedUrl && !loading && !error && (
          <span className='text-muted small'>No signature</span>
        )}
      </div>
      <div className='small text-muted mt-1'>{label}</div>
    </div>
  )
}

export const OSISignatureImagesTest: React.FC<{
  terpSignUrl?: string | null
  reqSignUrl?: string | null
}> = ({terpSignUrl, reqSignUrl}) => {
  return (
    <div className='row g-2'>
      {terpSignUrl && (
        <div className='col-md-6'>
          <VOSSignatureImage fileUrl={terpSignUrl} label='Interpreter signature' />
        </div>
      )}
      {reqSignUrl && (
        <div className='col-md-6'>
          <VOSSignatureImage fileUrl={reqSignUrl} label='Requester signature' />
        </div>
      )}
    </div>
  )
}
